import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { forgetSearch, getSearchHistory, rememberSearch } from '../api/endpoints';
import { SearchIcon } from './icons.js';

/**
 * Строка поиска с историей запросов.
 *
 * История живёт на сервере и привязана к зрителю, а не к браузеру: искал на
 * телевизоре — подсказка нужна и здесь. Поэтому список приходит запросом, а не
 * из localStorage, и обновляется тем же ответом, которым записывается.
 *
 * Подсказка показывает не «что ещё бывает», а «что вы уже искали», и оформлена
 * ровно так: янтарём выделено то, что вы набрали, приглушённым — остальное.
 * Обычный автокомплит подсвечивает наоборот, но здесь строка существует именно
 * потому, что совпала с набранным, и подсветка объясняет, почему она тут.
 */
export function SearchField({
  query,
  onQuery,
  onCommit,
}: {
  query: string;
  onQuery: (value: string) => void;
  /** Запрос принят: набрали и нажали ввод или выбрали из истории. */
  onCommit?: (value: string) => void;
}): JSX.Element {
  const [history, setHistory] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void getSearchHistory()
      .then((response) => setHistory(response.queries))
      // История — удобство, а не содержание: молча живём без неё.
      .catch(() => setHistory([]));
  }, []);

  // Клик мимо закрывает подсказку: без этого она остаётся висеть над каталогом.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent): void => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const typed = query.trim().toLocaleLowerCase('ru');
  // Сперва те, что начинаются с набранного, потом просто содержащие его: «ава»
  // должно в первую очередь напоминать «аватар», а не «неудачная авария».
  const matches = history
    .filter((entry) => entry.toLocaleLowerCase('ru').includes(typed))
    .sort((a, b) => {
      const first = a.toLocaleLowerCase('ru').startsWith(typed) ? 0 : 1;
      const second = b.toLocaleLowerCase('ru').startsWith(typed) ? 0 : 1;
      return first - second;
    })
    .slice(0, 8);

  const commit = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed === '') return;
    onQuery(trimmed);
    setOpen(false);
    setActive(-1);
    onCommit?.(trimmed);
    void rememberSearch(trimmed)
      .then((response) => setHistory(response.queries))
      .catch(() => undefined);
  };

  const forget = (value: string): void => {
    setHistory((prev) => prev.filter((entry) => entry !== value));
    void forgetSearch(value)
      .then((response) => setHistory(response.queries))
      .catch(() => undefined);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' && matches.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index + 1) % matches.length);
      return;
    }
    if (event.key === 'ArrowUp' && matches.length > 0) {
      event.preventDefault();
      setActive((index) => (index <= 0 ? matches.length - 1 : index - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(active >= 0 ? (matches[active] ?? query) : query);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div
      className="search-field"
      ref={box}
      // Фокус ушёл Tab'ом за пределы поля и подсказки — подсказка закрывается,
      // как и от клика мимо: иначе она висит над карточкой, на которой фокус.
      onBlur={(event) => {
        if (!box.current?.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
          setActive(-1);
        }
      }}
    >
      <SearchIcon className="search-field-icon" />
      <input
        ref={input}
        aria-label="Поиск"
        aria-expanded={open && matches.length > 0}
        role="combobox"
        aria-controls="search-history"
        value={query}
        onChange={(e) => {
          onQuery(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Найти фильм или сериал"
      />

      {open && matches.length > 0 && (
        <ul className="search-history" id="search-history" role="listbox">
          {matches.map((entry, index) => (
            <li key={entry}>
              <button
                type="button"
                role="option"
                aria-selected={index === active}
                className={index === active ? 'search-history-row active' : 'search-history-row'}
                onMouseEnter={() => setActive(index)}
                onClick={() => commit(entry)}
              >
                <span className="search-history-mark" aria-hidden="true">
                  ↩
                </span>
                <span className="search-history-text">{highlight(entry, typed)}</span>
              </button>
              <button
                type="button"
                className="search-history-forget"
                aria-label={`Забыть запрос «${entry}»`}
                onClick={() => forget(entry)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Набранная часть — янтарём, остальное приглушённым. */
function highlight(entry: string, typed: string): JSX.Element {
  const at = typed === '' ? -1 : entry.toLocaleLowerCase('ru').indexOf(typed);
  if (at < 0) return <>{entry}</>;
  return (
    <>
      {entry.slice(0, at)}
      <mark>{entry.slice(at, at + typed.length)}</mark>
      {entry.slice(at + typed.length)}
    </>
  );
}
